import type {
  BodyType,
  CreateIntegrationRequest,
  CreatePatient,
  DocumentPatientDto,
  IntegrationRequest,
  IntegrationRequestBundle,
  IntegrationRequestDto,
  IntegrationRequestUpdateOperation,
  Patient,
  ProfessionalDto,
} from '@domain/types';
import { Http } from '@services/http/http.service';

import type { IntegrationRequestID } from '@store/documents/documents.state';

const path = '/integrations';

export async function fetchIntegrationRequest(id: string): Promise<IntegrationRequestBundle> {
  return await Http.getInstance().get({ path: `${path}/${id}` });
}

export async function cancelIntegrationRequests(
  ids: IntegrationRequestID[],
): Promise<IntegrationRequest[]> {
  return await Http.getInstance().post({
    path: `${path}/cancel`,
    body: { ids },
  });
}

export async function cancelIntegrationRequest(
  id: IntegrationRequestID,
): Promise<IntegrationRequest> {
  return await Http.getInstance().put({
    path: `${path}/${id}/cancel`,
  });
}

export async function createIntegrationRequest(
  document: CreateIntegrationRequest,
): Promise<{ id: string }> {
  const formData = new FormData();
  const requestBody: Record<string, any> = {};

  for (const [key, value] of Object.entries(document)) {
    if (key === 'file') {
      if (document.file) formData.append('file', document.file, document.file.name);
    } else {
      requestBody[key] = value;
    }
  }

  formData.append('document', JSON.stringify(requestBody));

  return await Http.getInstance().postMultiFormData({
    path: `${path}`,
    body: formData,
  });
}

export async function removeIntegrationRequestPatient(
  docId: IntegrationRequest['id'],
): Promise<IntegrationRequestBundle> {
  return await Http.getInstance().destroy({ path: `${path}/${docId}/patient` });
}

export async function createIntegrationRequestPatient(
  docId: IntegrationRequestID,
  patient: CreatePatient,
  shouldRefresh = false,
): Promise<IntegrationRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/patient`,
    body: { patient, shouldRefresh },
  });
}

export async function addIntegrationRequestPatient(
  docId: IntegrationRequestID,
  patient: DocumentPatientDto,
  shouldRefresh = false,
): Promise<IntegrationRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${docId}/patient`,
    body: { patient, shouldRefresh },
  });
}

export async function updateIntegrationRequestPatient(
  docId: IntegrationRequestID,
  patient: Patient,
): Promise<{ document: IntegrationRequestDto; patient: Patient }> {
  return await Http.getInstance().put({
    path: `${path}/${docId}/patient`,
    body: { patient },
  });
}

export async function refreshIntegrationRequestPatientMatch(
  docId: IntegrationRequestID,
): Promise<undefined> {
  return await Http.getInstance().get({ path: `${path}/${docId}/integrations/refresh` });
}

export async function updateIntegrationRequest(
  docId: IntegrationRequestID,
  patch: IntegrationRequestUpdateOperation,
): Promise<IntegrationRequestBundle> {
  return await Http.getInstance().put({ path: `${path}/${docId}`, body: patch });
}

export type IntegrationRequestBulkUpdatePayload = {
  identityReference: string;
  updates: {
    id: string;
    uuid: string;
    documentType?: string;
    documentTitle?: string;
  }[];
};

export async function updateIntegrationRequests(payload: IntegrationRequestBulkUpdatePayload) {
  return await Http.getInstance().put({
    path: `${path}/update_multiple`,
    body: payload as BodyType,
  });
}

export async function downloadIntegrationRequests(ids: IntegrationRequestID[]): Promise<void> {
  return await Http.getInstance().download(
    {
      path: `${path}/download`,
      body: { ids },
    },
    'POST',
  );
}

export async function switchIdentityIntegrationRequest(
  id: IntegrationRequest['id'],
  sender: ProfessionalDto,
): Promise<IntegrationRequestBundle> {
  return await Http.getInstance().post({
    path: `${path}/${id}/switch_identity`,
    body: { sender },
  });
}

export async function duplicateIntegrationRequest(
  id: IntegrationRequest['id'],
): Promise<IntegrationRequestDto> {
  return await Http.getInstance().post({
    path: `${path}/${id}/duplicate`,
  });
}

export async function duplicateIntegrationRequests(ids: IntegrationRequest['id'][]): Promise<void> {
  return await Http.getInstance().post({
    path: `${path}/duplicate`,
    body: { ids },
  });
}
